import React, { useState, useEffect, useRef } from 'react';
import { optimizeLinkedIn } from '../services/openaiService';
import { parseResumeFile } from '../services/resumeParserService';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './LinkedInOptimizerPage.css';
import ProductSidebar from './ProductSidebar';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const SECTIONS = [
  { id: 'headline', label: 'Headline', icon: '✏️' },
  { id: 'summary', label: 'About / Summary', icon: '📝' },
  { id: 'experience', label: 'Experience', icon: '💼' },
  { id: 'skills', label: 'Skills', icon: '⚡' },
  { id: 'education', label: 'Education', icon: '🎓' },
];

const LinkedInOptimizerPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showAuthModal, setShowAuthModal] = useState(false);

  /* ── Profile data shared by both panels ─────────────────────────── */
  const [profileData, setProfileData] = useState(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);

  /* ── Headshot panel state ───────────────────────────────────────── */
  const [headshotSource, setHeadshotSource] = useState('profile'); // 'profile' | 'upload'
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState(null);
  const [generatedHeadshot, setGeneratedHeadshot] = useState(null);
  const [isGeneratingHeadshot, setIsGeneratingHeadshot] = useState(false);
  const [headshotError, setHeadshotError] = useState('');
  const photoInputRef = useRef(null);

  /* ── Optimizer panel state ──────────────────────────────────────── */
  const [targetRole, setTargetRole] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');

  const [resumeSource, setResumeSource] = useState('profile');     // 'profile' | 'upload'
  const [uploadedResumeName, setUploadedResumeName] = useState('');
  const [parsedResume, setParsedResume] = useState(null);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const resumeInputRef = useRef(null);

  const [results, setResults] = useState({});
  const [overallScore, setOverallScore] = useState(null);
  const [activeSection, setActiveSection] = useState('headline');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({});

  /* ── Fetch profile on mount ──────────────────────────────────────── */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled || !data?.profile) return;
        setProfileData(data.profile);
        setProfileAvatarUrl(data.profile.avatarUrl || null);
        if (data.profile.jobTitle) setTargetRole(prev => prev || data.profile.jobTitle);
        // If no saved avatar, default the headshot source to "upload".
        if (!data.profile.avatarUrl) setHeadshotSource('upload');
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => () => {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
  }, [uploadedPreviewUrl]);

  /* ───────────────────────────── Headshot ───────────────────────── */

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setHeadshotError('Please choose an image file (PNG, JPG, WEBP).');
      return;
    }
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPhoto(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    setHeadshotError('');
    setGeneratedHeadshot(null);
  };

  const handleGenerateHeadshot = async () => {
    if (!user) { setShowAuthModal(true); return; }
    setHeadshotError('');
    setGeneratedHeadshot(null);

    const form = new FormData();
    if (headshotSource === 'upload') {
      if (!uploadedPhoto) {
        setHeadshotError('Choose a photo to upload first.');
        return;
      }
      form.append('photo', uploadedPhoto);
    } else {
      if (!profileAvatarUrl) {
        setHeadshotError('No profile picture on file — upload a photo instead.');
        return;
      }
      const fname = profileAvatarUrl.split('/').pop();
      form.append('avatarPath', fname);
    }

    setIsGeneratingHeadshot(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/headshot/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok || !data?.image) throw new Error(data?.message || 'Headshot generation failed.');
      setGeneratedHeadshot(data.image);
    } catch (err) {
      setHeadshotError(err.message || 'Headshot generation failed.');
    } finally {
      setIsGeneratingHeadshot(false);
    }
  };

  const downloadHeadshot = () => {
    if (!generatedHeadshot) return;
    const a = document.createElement('a');
    a.href = generatedHeadshot;
    const base = (profileData?.fullName || user?.name || 'headshot').replace(/\s+/g, '_');
    a.download = `${base}_headshot.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const headshotSourceReady =
    headshotSource === 'profile' ? !!profileAvatarUrl : !!uploadedPhoto;

  /* ───────────────────────────── Optimizer ──────────────────────── */

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setError('');
    setUploadedResumeName(file.name);
    setIsParsingResume(true);
    try {
      const parsed = await parseResumeFile(file, '', []);
      if (!parsed) throw new Error('Could not read the resume.');
      const adapted = {
        fullName: parsed.personalInfo?.name || '',
        email:    parsed.personalInfo?.email || '',
        phone:    parsed.personalInfo?.phone || '',
        location: parsed.personalInfo?.location || '',
        linkedIn: parsed.personalInfo?.linkedin || '',
        portfolio: parsed.personalInfo?.portfolio || '',
        professionalSummary: parsed.summary || '',
        workExperience: parsed.workExperience || [],
        education: parsed.education || [],
        skills: parsed.skills || '',
        projects: parsed.projects || [],
        certifications: parsed.certifications || '',
      };
      setParsedResume(adapted);
    } catch (err) {
      setError(`Resume parse failed: ${err.message}`);
      setUploadedResumeName('');
    } finally {
      setIsParsingResume(false);
    }
  };

  const handleOptimize = async () => {
    if (!user) { setShowAuthModal(true); return; }
    if (!targetRole.trim()) {
      setError('Please enter your target role.');
      return;
    }
    const activeProfile = resumeSource === 'upload' ? parsedResume : profileData;
    if (resumeSource === 'upload' && !parsedResume) {
      setError('Upload and wait for the resume to finish parsing.');
      return;
    }

    setIsOptimizing(true);
    setError('');
    setResults({});
    setOverallScore(null);
    try {
      // Shared LinkedIn Optimizer + Headshot pool — 5 free uses per user
      // across both actions. Deduct before invoking OpenAI; active
      // subscribers bypass on the backend.
      const dr = await fetch(`${API_BASE_URL}/api/linkedin-optimizer/deduct-credit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ kind: 'optimize' }),
      });
      if (!dr.ok) {
        const d = await dr.json().catch(() => ({}));
        throw new Error(d?.error || 'Out of LinkedIn Optimizer credits — upgrade to continue.');
      }
      const data = await optimizeLinkedIn('', targetRole, targetIndustry, '', activeProfile);
      setResults(data.optimizations || {});
      setOverallScore(typeof data?.overallScore === 'number' ? data.overallScore : null);
      const firstReady = SECTIONS.find(s => data.optimizations?.[s.id]);
      if (firstReady) setActiveSection(firstReady.id);
    } catch (err) {
      setError(err.message || 'Failed to optimize. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleCopy = (sectionId, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied((prev) => ({ ...prev, [sectionId]: true }));
    setTimeout(() => setCopied((prev) => ({ ...prev, [sectionId]: false })), 2000);
  };

  const hasResults = Object.keys(results).length > 0;
  const optimizerReady =
    targetRole.trim() &&
    (resumeSource === 'profile' ? !!profileData : !!parsedResume);

  return (
    <div className={embedded ? 'li-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background"></div>}
      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      <div
        className={`li-page-container ${embedded ? 'li-page-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/linkedin-optimizer.png')" }}
      >
        {!embedded && <Header setShowAuthModal={setShowAuthModal} />}
        {!embedded && <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />}

        <main className="li-page-main">
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                LinkedIn <span className="li-gradient-text">Profile Optimizer</span>
              </h1>
              <p className="li-hero-subtitle">
                Generate a professional headshot and a recruiter-ready profile — in under a minute.
              </p>
            </div>
          )}

          <div className="li-main-grid li-main-grid--two">
            {/* ─────────────── LEFT: Headshot Studio ─────────────── */}
            <div className="li-headshot-panel">
              <div className="li-card li-headshot-card">
                <div className="li-card-header">
                  <span className="li-card-icon">📸</span>
                  <h3>AI Headshot Studio</h3>
                </div>
                <p className="li-card-sub">
                  Use your profile picture or upload a new photo — we'll generate
                  a polished, recruiter-ready headshot.
                </p>

                {/* Source picker */}
                <div className="li-source-row">
                  <label className={`li-source-opt ${headshotSource === 'profile' ? 'is-active' : ''} ${!profileAvatarUrl ? 'is-disabled' : ''}`}>
                    <input
                      type="radio"
                      name="headshot-src"
                      checked={headshotSource === 'profile'}
                      onChange={() => setHeadshotSource('profile')}
                      disabled={!profileAvatarUrl || isGeneratingHeadshot}
                    />
                    <span>Use profile picture</span>
                    {!profileAvatarUrl && <small>none on file</small>}
                  </label>
                  <label className={`li-source-opt ${headshotSource === 'upload' ? 'is-active' : ''}`}>
                    <input
                      type="radio"
                      name="headshot-src"
                      checked={headshotSource === 'upload'}
                      onChange={() => setHeadshotSource('upload')}
                      disabled={isGeneratingHeadshot}
                    />
                    <span>Upload new photo</span>
                  </label>
                </div>

                {/* Source + result slots */}
                <div className="li-headshot-stage">
                  <div className="li-headshot-slot">
                    <div className="li-headshot-slot-label">Source</div>
                    {headshotSource === 'profile' && profileAvatarUrl ? (
                      <img
                        className="li-headshot-img"
                        src={`${API_BASE_URL}${profileAvatarUrl}`}
                        alt="Profile"
                      />
                    ) : headshotSource === 'upload' && uploadedPreviewUrl ? (
                      <img
                        className="li-headshot-img"
                        src={uploadedPreviewUrl}
                        alt="Upload preview"
                      />
                    ) : (
                      <button
                        type="button"
                        className="li-headshot-empty"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={isGeneratingHeadshot || headshotSource === 'profile'}
                      >
                        <span style={{ fontSize: 24 }}>📁</span>
                        <span>{headshotSource === 'upload' ? 'Choose photo' : 'No photo'}</span>
                      </button>
                    )}
                    {headshotSource === 'upload' && uploadedPreviewUrl && (
                      <button
                        type="button"
                        className="li-headshot-replace"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={isGeneratingHeadshot}
                      >
                        Replace
                      </button>
                    )}
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
                    />
                  </div>

                  <div className="li-headshot-slot">
                    <div className="li-headshot-slot-label">Generated</div>
                    {generatedHeadshot ? (
                      <img
                        className="li-headshot-img"
                        src={generatedHeadshot}
                        alt="Generated headshot"
                      />
                    ) : (
                      <div className={`li-headshot-empty is-static ${isGeneratingHeadshot ? 'is-busy' : ''}`}>
                        {isGeneratingHeadshot ? (
                          <>
                            <span className="li-spinner" />
                            <span>Generating…</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 24 }}>✨</span>
                            <span>Result appears here</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {headshotError && <div className="li-error">{headshotError}</div>}

                <div className="li-headshot-actions">
                  <button
                    type="button"
                    className="li-optimize-btn"
                    onClick={handleGenerateHeadshot}
                    disabled={isGeneratingHeadshot || !headshotSourceReady}
                  >
                    {isGeneratingHeadshot ? '✨ Generating headshot…' : '✨ Generate Headshot'}
                  </button>
                  {generatedHeadshot && (
                    <button
                      type="button"
                      className="li-secondary-btn"
                      onClick={downloadHeadshot}
                    >
                      ⬇️ Download
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ─────────────── RIGHT: Profile Optimizer ─────────────── */}
            <div className="li-optimizer-panel">
              <div className="li-card">
                <div className="li-card-header">
                  <span className="li-card-icon">🎯</span>
                  <h3>Target Position</h3>
                </div>
                <div className="li-field-group">
                  <label className="li-label">Target Role *</label>
                  <input
                    type="text"
                    className="li-input"
                    value={targetRole}
                    onChange={(e) => { setTargetRole(e.target.value); setError(''); }}
                    placeholder="e.g. Senior Software Engineer"
                    disabled={isOptimizing}
                  />
                </div>
                <div className="li-field-group" style={{ marginTop: '14px' }}>
                  <label className="li-label">Target Industry</label>
                  <input
                    type="text"
                    className="li-input"
                    value={targetIndustry}
                    onChange={(e) => setTargetIndustry(e.target.value)}
                    placeholder="e.g. FinTech, Healthcare, SaaS"
                    disabled={isOptimizing}
                  />
                </div>
              </div>

              <div className="li-card">
                <div className="li-card-header">
                  <span className="li-card-icon">📄</span>
                  <h3>Resume Source</h3>
                </div>
                <div className="li-source-row">
                  <label className={`li-source-opt ${resumeSource === 'profile' ? 'is-active' : ''} ${!profileData ? 'is-disabled' : ''}`}>
                    <input
                      type="radio"
                      name="resume-src"
                      checked={resumeSource === 'profile'}
                      onChange={() => setResumeSource('profile')}
                      disabled={!profileData || isOptimizing}
                    />
                    <span>Use profile data</span>
                  </label>
                  <label className={`li-source-opt ${resumeSource === 'upload' ? 'is-active' : ''}`}>
                    <input
                      type="radio"
                      name="resume-src"
                      checked={resumeSource === 'upload'}
                      onChange={() => setResumeSource('upload')}
                      disabled={isOptimizing}
                    />
                    <span>Upload resume</span>
                  </label>
                </div>
                {resumeSource === 'upload' && (
                  <div className="li-resume-upload">
                    <input
                      ref={resumeInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      hidden
                      onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className="li-secondary-btn"
                      onClick={() => resumeInputRef.current?.click()}
                      disabled={isParsingResume || isOptimizing}
                    >
                      {isParsingResume
                        ? 'Parsing…'
                        : uploadedResumeName
                          ? `Replace (${uploadedResumeName})`
                          : '📄 Choose PDF / DOCX'}
                    </button>
                    {parsedResume && !isParsingResume && (
                      <span className="li-resume-ok">✅ Parsed</span>
                    )}
                  </div>
                )}
              </div>

              {error && <div className="li-error">{error}</div>}

              <button
                className="li-optimize-btn"
                onClick={handleOptimize}
                disabled={isOptimizing || !optimizerReady}
              >
                {isOptimizing ? (
                  <span className="li-loading">
                    <span className="typing-indicator">
                      <span></span><span></span><span></span>
                    </span>
                    Optimizing Profile...
                  </span>
                ) : (
                  '✨ Optimize My LinkedIn'
                )}
              </button>

              {/* Results section appears below inputs once ready */}
              {overallScore !== null && (
                <div className="li-score-card">
                  <div className="li-score-circle">
                    <svg viewBox="0 0 120 120" className="li-score-svg">
                      <circle cx="60" cy="60" r="50" className="li-score-bg" />
                      <circle
                        cx="60" cy="60" r="50"
                        className="li-score-fill"
                        strokeDasharray={`${(overallScore / 100) * 314} 314`}
                      />
                    </svg>
                    <div className="li-score-value">{overallScore}</div>
                  </div>
                  <div className="li-score-info">
                    <h3>Profile Score</h3>
                    <p>{overallScore >= 80 ? '🔥 Excellent profile strength!' : overallScore >= 60 ? '👍 Good — apply suggestions below' : '⚡ Needs improvement — use the suggestions'}</p>
                  </div>
                </div>
              )}

              {hasResults && (
                <div className="li-tabs">
                  {SECTIONS.map((s) => (
                    <button
                      key={s.id}
                      className={`li-tab ${activeSection === s.id ? 'li-tab--active' : ''} ${results[s.id] ? 'li-tab--has-result' : ''}`}
                      onClick={() => setActiveSection(s.id)}
                    >
                      <span>{s.icon}</span>
                      <span>{s.label}</span>
                      {results[s.id] && <span className="li-tab-dot" />}
                    </button>
                  ))}
                </div>
              )}

              {hasResults && results[activeSection] ? (
                <div className="li-result-card">
                  <div className="li-result-header">
                    <h3>{SECTIONS.find(s => s.id === activeSection)?.icon} {SECTIONS.find(s => s.id === activeSection)?.label} Optimization</h3>
                    <button
                      className="li-copy-btn"
                      onClick={() => handleCopy(activeSection, results[activeSection]?.optimized || '')}
                    >
                      {copied[activeSection] ? '✅ Copied!' : '📋 Copy'}
                    </button>
                  </div>

                  {results[activeSection]?.issues?.length > 0 && (
                    <div className="li-issues">
                      <h4>⚠️ Current Issues</h4>
                      <ul>
                        {results[activeSection].issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="li-optimized-content">
                    <h4>✨ Optimized Version</h4>
                    <div className="li-optimized-text">
                      {results[activeSection]?.optimized}
                    </div>
                  </div>

                  {results[activeSection]?.tips?.length > 0 && (
                    <div className="li-tips">
                      <h4>💡 Pro Tips</h4>
                      <ul>
                        {results[activeSection].tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results[activeSection]?.keywords?.length > 0 && (
                    <div className="li-keywords">
                      <h4>🔑 Keywords to Include</h4>
                      <div className="li-keyword-chips">
                        {results[activeSection].keywords.map((kw, i) => (
                          <span key={i} className="li-keyword-chip">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {!embedded && (
            <section className="li-info-section">
              <h2 className="li-section-title">Why Optimize Your LinkedIn?</h2>
              <div className="li-info-grid">
                {[
                  { icon: '📸', title: 'A Headshot That Lands Interviews', desc: 'First impression is the photo. Our AI gives you a polished, studio-quality portrait recruiters can\'t scroll past.' },
                  { icon: '🎯', title: 'Beat the Algorithm', desc: 'LinkedIn ranks profiles by keyword relevance. Our AI ensures your profile is packed with the right terms recruiters search for.' },
                  { icon: '👀', title: 'Get 5x More Views', desc: 'Optimized profiles receive dramatically more recruiter views. A strong headline and summary are your first impression.' },
                  { icon: '💼', title: 'Land Better Interviews', desc: 'Recruiters spend just 7 seconds on a profile. Make every word count with AI-crafted, compelling content.' },
                ].map((item, i) => (
                  <div key={i} className="li-info-card">
                    <div className="li-info-icon">{item.icon}</div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>

        {!embedded && <Footer />}
      </div>
    </div>
  );
};

export default LinkedInOptimizerPage;
